import type { ChildDaySummary, TimelineItem } from '@baby-tracker/contracts';
import { millilitresToVolume, type VolumeDisplayUnit } from '@baby-tracker/validation';

import { nappyTypeLabels, sleepKindLabels } from './labels.js';

export function formatDuration(totalSeconds: number): string {
    const seconds = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    if (minutes > 0) {
        return `${minutes}m`;
    }
    return `${seconds}s`;
}

export function formatVolume(millilitres: number, unit: VolumeDisplayUnit): string {
    const value = millilitresToVolume(millilitres, unit);
    return unit === 'ml' ? `${value} ml` : `${value} oz`;
}

export function formatWhen(instant: string, timeZone: string): string {
    return new Intl.DateTimeFormat(undefined, {
        timeZone,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(new Date(instant));
}

export function summaryLine(row: ChildDaySummary, unit: VolumeDisplayUnit): string {
    const parts: string[] = [];
    if (row.breastSeconds > 0) {
        parts.push(`Breast ${formatDuration(row.breastSeconds)}`);
    }
    if (row.bottleMl > 0) {
        parts.push(`Bottle ${formatVolume(row.bottleMl, unit)}`);
    }
    if (row.solidCount > 0) {
        parts.push(row.solidCount === 1 ? '1 solid' : `${row.solidCount} solids`);
    }
    if (row.sleepSeconds > 0) {
        parts.push(`Sleep ${formatDuration(row.sleepSeconds)}`);
    }
    const nappies = row.nappies.wet + row.nappies.dirty + row.nappies.mixed + row.nappies.dry;
    if (nappies > 0) {
        parts.push(nappies === 1 ? '1 nappy' : `${nappies} nappies`);
    }
    return parts.length > 0 ? parts.join(' · ') : 'Nothing logged yet';
}

export function timelineLabel(
    item: TimelineItem,
    unit: VolumeDisplayUnit,
): { title: string; detail: string } {
    switch (item.type) {
        case 'feeding':
            if (item.feeding.method === 'bottle') {
                return { title: 'Bottle', detail: formatVolume(item.feeding.volumeMl ?? 0, unit) };
            }
            if (item.feeding.method === 'solid') {
                return { title: 'Solid', detail: item.feeding.foodDescription ?? 'Food' };
            }
            return {
                title: 'Breast',
                detail:
                    item.feeding.endAt === null
                        ? 'Running'
                        : formatDuration(
                              (item.feeding.leftDurationS ?? 0) +
                                  (item.feeding.rightDurationS ?? 0),
                          ),
            };
        case 'pumping':
            return {
                title: 'Pump',
                detail:
                    item.pumping.endAt === null
                        ? 'Running'
                        : formatVolume(item.pumping.totalMl ?? 0, unit),
            };
        case 'sleep': {
            const kind = sleepKindLabels[item.sleep.kind];
            return {
                title: 'Sleep',
                detail:
                    item.sleep.endAt === null
                        ? `${kind} · Running`
                        : `${kind} · ${formatDuration(
                              Math.max(
                                  0,
                                  Math.floor(
                                      (Date.parse(item.sleep.endAt) -
                                          Date.parse(item.sleep.startAt)) /
                                          1000,
                                  ),
                              ),
                          )}`,
            };
        }
        case 'nappy':
            return { title: 'Nappy', detail: nappyTypeLabels[item.nappy.kind] };
    }
}

export function elapsedSeconds(startAt: string, now: number): number {
    const start = Date.parse(startAt);
    if (!Number.isFinite(start)) {
        return 0;
    }
    return Math.max(0, Math.floor((now - start) / 1000));
}
